import * as THREE from 'three';

interface ParticleEffect {
  points: THREE.Points;
  velocities: Float32Array;
  life: number;
  maxLife: number;
  gravity: number;
}

/**
 * Expanding-ring effect used for flashy, non-particle feedback (level-ups,
 * etc). Unlike particle effects the mesh scales up while fading, giving a
 * clean "shockwave" look.
 */
interface RingEffect {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
  startScale: number;
  endScale: number;
  /** Rotation delta per second, applied to ring.rotation.z. */
  spin: number;
  /** Optional follow-target — when set, the ring tracks this object. */
  target?: THREE.Object3D;
  /** World-space anchor used when no follow-target is provided. */
  anchor: THREE.Vector3;
}

/** Short-lived emissive shell that ignores depth — used when the logical hit
 *  point sits *inside* the visible hull (collision sphere < procedural mesh). */
interface HitFlashEffect {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
  startScale: number;
  endScale: number;
  anchor: THREE.Vector3;
  baseOpacity: number;
}

// High enough for Gatling + merged-HP svo chains without evicting active bursts.
const MAX_EFFECTS = 120;
const MAX_HIT_FLASHES = 80;

export class EffectsManager {
  private effects: ParticleEffect[] = [];
  private rings: RingEffect[] = [];
  private hitFlashes: HitFlashEffect[] = [];
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  spawnImpact(position: THREE.Vector3, color: number, count = 20): void {
    this.spawn(position, color, count, 0.8, 15, 0);
  }

  /**
   * Compact "mini explosion" used at projectile-hit sites. Combines a hot
   * white core sparkle, a colored particle burst, and a single short-lived
   * shock ring so even point-blank shots — where the projectile entity
   * never reaches the client — still produce visible impact feedback.
   *
   * When `opts.compact` is true (used for hits landing on the LOCAL
   * player's ship), every flash / ring / particle scale is halved. The
   * rationale: when an NPC opens fire on you, the camera is clamped
   * just behind your own hull, so the full-size effect was occluding
   * the target you're trying to aim at. The compact variant is loud
   * enough to read as a hit but small enough to keep the NPC visible
   * through the smoke. PvP-symmetric: the same downscale fires on
   * every player target regardless of who shot them, so the visual
   * advantage is purely "your camera is on this ship, so cull the
   * fireworks".
   */
  spawnMiniExplosion(
    position: THREE.Vector3,
    color: number = 0xffaa22,
    opts?: { compact?: boolean },
  ): void {
    const k = opts?.compact ? 0.5 : 1;
    // Always-visible shell: the server hit is on a sphere that can be *inside*
    // the drawn ship, so point sprites would fail the depth test against hull polys.
    this.spawnHitFlash(position, 0xffeeff, { endScale: 4.5 * k, life: 0.22 });
    this.spawnHitFlash(position, color, { endScale: 6 * k, life: 0.28, dim: true });
    this.spawn(position, 0xffffff, opts?.compact ? 8 : 16, 0.45, 20 * k, 0);
    this.spawn(position, color, opts?.compact ? 12 : 26, 0.75, 16 * k, 0);
    this.spawnRing(position, 0xffffff, 0.8 * k, 6.5 * k, 0.5, 5.0);
    this.spawnRing(position, color, 1.2 * k, 8.5 * k, 0.55, 4.5);
  }

  spawnDestruction(position: THREE.Vector3, color: number, count = 60): void {
    this.spawnHitFlash(position, 0xff8844, { endScale: 14, life: 0.4 });
    this.spawn(position, color, count, 1.5, 25, 0);
  }

  spawnPickup(position: THREE.Vector3, color: number, count = 15): void {
    this.spawn(position, color, count, 0.6, 8, -10);
  }

  /**
   * Big celebratory burst for player level-ups. Combines a slow golden
   * particle fountain, an upward sparkle plume, and two expanding shock
   * rings. When `target` is provided the rings follow that object so the
   * effect stays pinned to a moving ship.
   */
  spawnLevelUp(
    position: THREE.Vector3,
    color: number = 0xffdd44,
    target?: THREE.Object3D,
  ): void {
    // Slow gold fountain — the bulk of the particle feel.
    this.spawn(position, color, 60, 1.8, 14, -4);
    // Bright white core sparkles on top for contrast.
    this.spawn(position, 0xffffff, 28, 1.2, 20, -2);
    // Two stacked shockwave rings, slightly out of phase.
    this.spawnRing(position, color, 0.6, 10, 2.0, 1.5, target);
    this.spawnRing(position, 0xffffff, 0.4, 7, 1.2, -2.0, target);
  }

  private spawnRing(
    position: THREE.Vector3,
    color: number,
    startScale: number,
    endScale: number,
    life: number,
    spin: number,
    target?: THREE.Object3D,
  ): void {
    if (this.rings.length >= MAX_EFFECTS) {
      const oldest = this.rings.shift()!;
      this.scene.remove(oldest.mesh);
      oldest.mesh.geometry.dispose();
      (oldest.mesh.material as THREE.Material).dispose();
    }

    const geometry = new THREE.RingGeometry(0.95, 1.0, 48);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      fog: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.scale.setScalar(startScale);
    this.scene.add(mesh);

    this.rings.push({
      mesh,
      life,
      maxLife: life,
      startScale,
      endScale,
      spin,
      target,
      anchor: position.clone(),
    });
  }

  private spawn(
    position: THREE.Vector3,
    color: number,
    count: number,
    life: number,
    speed: number,
    gravity: number,
  ): void {
    if (this.effects.length >= MAX_EFFECTS) {
      const oldest = this.effects.shift()!;
      this.scene.remove(oldest.points);
      oldest.points.geometry.dispose();
      (oldest.points.material as THREE.Material).dispose();
    }

    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y;
      positions[i * 3 + 2] = position.z;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const s = speed * (0.3 + Math.random() * 0.7);
      velocities[i * 3] = Math.sin(phi) * Math.cos(theta) * s;
      velocities[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * s;
      velocities[i * 3 + 2] = Math.cos(phi) * s;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color,
      // Large enough to read at combat range; scene fog (see GameScene) would
      // fully wash out the old 0.4pt sparks unless we opt out of fog.
      size: 1.25,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      sizeAttenuation: true,
      fog: false,
    });

    const points = new THREE.Points(geometry, material);
    this.scene.add(points);

    this.effects.push({ points, velocities, life, maxLife: life, gravity });
  }

  update(dt: number): void {
    const toRemove: number[] = [];

    for (let i = 0; i < this.effects.length; i++) {
      const effect = this.effects[i];
      effect.life -= dt;

      if (effect.life <= 0) {
        toRemove.push(i);
        continue;
      }

      const t = 1 - effect.life / effect.maxLife;
      const mat = effect.points.material as THREE.PointsMaterial;
      mat.opacity = 1 - t;
      mat.size = 1.25 * (1 - t * 0.5);

      const posAttr = effect.points.geometry.attributes['position'] as THREE.BufferAttribute;
      const pos = posAttr.array as Float32Array;
      const vel = effect.velocities;
      const count = posAttr.count;

      for (let j = 0; j < count; j++) {
        vel[j * 3 + 1] += effect.gravity * dt;
        pos[j * 3] += vel[j * 3] * dt;
        pos[j * 3 + 1] += vel[j * 3 + 1] * dt;
        pos[j * 3 + 2] += vel[j * 3 + 2] * dt;
      }
      posAttr.needsUpdate = true;
    }

    for (let i = toRemove.length - 1; i >= 0; i--) {
      const idx = toRemove[i];
      const effect = this.effects[idx];
      this.scene.remove(effect.points);
      effect.points.geometry.dispose();
      (effect.points.material as THREE.Material).dispose();
      this.effects.splice(idx, 1);
    }

    this.updateRings(dt);
    this.updateHitFlashes(dt);
  }

  private spawnHitFlash(
    position: THREE.Vector3,
    color: number,
    opts: { endScale: number; life: number; dim?: boolean },
  ): void {
    if (this.hitFlashes.length >= MAX_HIT_FLASHES) {
      const old = this.hitFlashes.shift()!;
      this.scene.remove(old.mesh);
      old.mesh.geometry.dispose();
      (old.mesh.material as THREE.Material).dispose();
    }

    const geo = new THREE.IcosahedronGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opts.dim ? 0.5 : 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      fog: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(position);
    const startScale = opts.dim ? 0.2 : 0.35;
    mesh.scale.setScalar(startScale);
    this.scene.add(mesh);
    const baseOpacity = opts.dim ? 0.45 : 0.95;
    this.hitFlashes.push({
      mesh,
      life: opts.life,
      maxLife: opts.life,
      startScale,
      endScale: opts.endScale,
      anchor: position.clone(),
      baseOpacity,
    });
  }

  private updateHitFlashes(dt: number): void {
    for (let i = this.hitFlashes.length - 1; i >= 0; i--) {
      const f = this.hitFlashes[i]!;
      f.life -= dt;
      if (f.life <= 0) {
        this.scene.remove(f.mesh);
        f.mesh.geometry.dispose();
        (f.mesh.material as THREE.Material).dispose();
        this.hitFlashes.splice(i, 1);
        continue;
      }
      const t = 1 - f.life / f.maxLife;
      f.mesh.position.copy(f.anchor);
      const s = f.startScale + (f.endScale - f.startScale) * t;
      f.mesh.scale.setScalar(s);
      f.mesh.rotation.x += 2.2 * dt;
      f.mesh.rotation.y += 1.4 * dt;
      (f.mesh.material as THREE.MeshBasicMaterial).opacity = f.baseOpacity * (1 - t);
    }
  }

  private updateRings(dt: number): void {
    const toRemove: number[] = [];
    for (let i = 0; i < this.rings.length; i++) {
      const r = this.rings[i];
      r.life -= dt;
      if (r.life <= 0) {
        toRemove.push(i);
        continue;
      }

      // Track the follow-target so the ring stays glued to moving ships.
      if (r.target) {
        r.mesh.position.copy(r.target.position);
      } else {
        r.mesh.position.copy(r.anchor);
      }

      const t = 1 - r.life / r.maxLife;
      const scale = r.startScale + (r.endScale - r.startScale) * t;
      r.mesh.scale.setScalar(scale);
      r.mesh.rotation.z += r.spin * dt;
      (r.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t);
    }
    for (let i = toRemove.length - 1; i >= 0; i--) {
      const idx = toRemove[i];
      const r = this.rings[idx];
      this.scene.remove(r.mesh);
      r.mesh.geometry.dispose();
      (r.mesh.material as THREE.Material).dispose();
      this.rings.splice(idx, 1);
    }
  }

  dispose(): void {
    for (const effect of this.effects) {
      this.scene.remove(effect.points);
      effect.points.geometry.dispose();
      (effect.points.material as THREE.Material).dispose();
    }
    this.effects.length = 0;
    for (const r of this.rings) {
      this.scene.remove(r.mesh);
      r.mesh.geometry.dispose();
      (r.mesh.material as THREE.Material).dispose();
    }
    this.rings.length = 0;
    for (const f of this.hitFlashes) {
      this.scene.remove(f.mesh);
      f.mesh.geometry.dispose();
      (f.mesh.material as THREE.Material).dispose();
    }
    this.hitFlashes.length = 0;
  }
}
